import AssignmentTypeSelect from "@/components/AssignmentTypeSelect";
import { DeleteButton } from "@/components/DeleteButton";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import Form from "@/components/Form";
import SaveButton from "@/components/SaveButton";
import UserSelect from "@/components/UserSelect";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import { SubordinatesFieldsets } from "@/modules/structure-assignments/components/SubordinatesFieldsets";
import { useSubordinateRole } from "@/modules/structure-assignments/hooks/useSubordinateRole";
import { useSupervisorRole } from "@/modules/structure-assignments/hooks/useSupervisorRole";
import { StructureAssignmentForm as StructureAssignmentFormType } from "@/modules/structure-assignments/types";
import { createEmptySubordinate } from "@/modules/structure-assignments/utils";
import {
  ASSIGNMENT_TYPES,
  writeStructureAssignment,
} from "@/schemas/structure-assignment";
import {
  ReadStructureAssignment,
  WriteStructureAssignment,
} from "@/types/structure-assignment";
import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { memo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ZodObject, ZodRawShape } from "zod";

const defaultValues: Partial<WriteStructureAssignment> = {
  supervisor_id: "",
  assignment_type: ASSIGNMENT_TYPES.RR_2_RD,
  subordinates: [createEmptySubordinate()],
};

export interface StructureAssignmentsFormProps {
  mutation: UseMutationResult<
    ReadStructureAssignment,
    Error,
    WriteStructureAssignment
  >;
  schema: ZodObject<ZodRawShape>;
  query?: UseQueryResult<StructureAssignmentFormType, Error>;
  disabled?: boolean;
  onDelete?: () => void;
}

export const StructureAssignmentsForm = memo(
  ({
    mutation,
    schema,
    query,
    onDelete,
    disabled,
  }: StructureAssignmentsFormProps) => {
    const { t } = useTranslation();

    const form = useForm({
      resolver: zodResolver(schema),
      defaultValues,
      disabled: mutation.isPending || disabled,
    });

    const assignmentType = useWatch({
      control: form.control,
      name: "assignment_type",
    });

    const supervisorRole = useSupervisorRole(assignmentType);
    const subordinateRole = useSubordinateRole(assignmentType);

    useFormUnsavedChangesWarning(form);
    useFormResetWithQueryData(form, query);

    return (
      <Form form={form} handleSubmit={mutation.mutateAsync}>
        <Card>
          <CardHeader>
            <CardTitle>{t("structureAssignments.form.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Fieldset className="grid grid-cols-2 gap-4">
              <Field
                name="supervisor_id"
                label={t("structureAssignments.fields.supervisor_id")}
                required={isRequired(
                  writeStructureAssignment.shape.supervisor_id,
                )}
                render={({ field }) => (
                  <UserSelect
                    systemRoleCodes={supervisorRole}
                    loading="eager"
                    loadingCondition={supervisorRole !== undefined}
                    {...field}
                    disabled={field.disabled || supervisorRole === undefined}
                    defaultItem={{
                      id: query?.data?.supervisor_id,
                      name: query?.data?.supervisor_name,
                    }}
                  />
                )}
              />
              <Field
                name="assignment_type"
                label={t("structureAssignments.fields.assignment_type")}
                required={isRequired(
                  writeStructureAssignment.shape.assignment_type,
                )}
                render={({ field }) => <AssignmentTypeSelect {...field} />}
              />
            </Fieldset>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              {t("structureAssignments.subordinates.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SubordinatesFieldsets
              role={subordinateRole}
              subordinates={query?.data?.subordinates}
            />
          </CardContent>
        </Card>
        <footer className="flex flex-col sm:flex-row gap-3 justify-end">
          {onDelete && (
            <DeleteButton
              onClick={onDelete}
              disabled={form.formState.disabled}
            />
          )}
          <SaveButton />
        </footer>
      </Form>
    );
  },
);
